
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Search, XCircle } from "lucide-react";
import { useEffect, useRef } from "react";

interface UnifiedSearchProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    shortcut?: boolean;
}

export function UnifiedSearch({
    value,
    onChange,
    placeholder = "Search...",
    className,
    shortcut = true
}: UnifiedSearchProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!shortcut) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Focus search with Ctrl+F (if not already in a text input/textarea)
            if (
                (e.ctrlKey || e.metaKey) &&
                e.key.toLowerCase() === "f" &&
                !["INPUT", "TEXTAREA"].includes(
                    (document.activeElement?.tagName || "").toUpperCase()
                )
            ) {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [shortcut]);

    return (
        <div className={cn("relative w-40 lg:w-64 group transition-all duration-200 focus-within:w-48 lg:focus-within:w-72 mr-1", className)}>
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
            <Input
                ref={inputRef}
                placeholder={placeholder}
                className="pl-9 pr-8 h-9 text-sm bg-muted/30 border-muted-foreground/10 focus:bg-background focus:border-primary/30 transition-all rounded-xl shadow-sm"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Escape") onChange("");
                }}
                type="search"
            />
            {value && (
                <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => onChange("")}
                    type="button"
                >
                    <XCircle className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}
